import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  TextField,
  Typography,
  useMediaQuery,
} from '@material-ui/core'
import { Controller, useForm } from 'react-hook-form'
import { Alert, Autocomplete } from '@material-ui/lab'
import { useTheme } from '@material-ui/styles'

import { Card, LoadingFeedback } from 'components'
import { ManageUsersTable } from './components'

import { useFetch, useSnackbar, usePagination } from 'hooks'

import helpers from 'helpers'
import * as service from 'service'
import schema from './schema'

const ManageUsersForm = ({
  ssoCorporateGroup = {},
  show = false,
  setRefreshAll,
  refreshAll,
}) => {
  const [userInputValue, setUserInputValue] = useState('')
  const [users, setUsers] = useState([])
  const [loadingUsers, setLoadingUsers] = useState(false)

  const snackbar = useSnackbar()
  const handleTypeUsersInput = (e) => {
    setUserInputValue(e.target.value)
  }
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'), {
    defaultMatches: true,
  })
  const { perPage, page, handleChangePage, handleChangeRowsPerPage } =
    usePagination(5)
  const { handleSubmit, control, errors } = useForm({
    validationSchema: schema,
  })

  const { instructionsText } = helpers.users.typeAndSearch

  const { response, isLoading, refresh } = useFetch(
    service.dponet.ssoCorporateGroupUsers.get,
    {
      ssoCorporateGroupId: ssoCorporateGroup?.id,
      userEmail: ssoCorporateGroup?.domain,
      perPage,
      page,
      minimal: true,
    },
    [perPage, page, ssoCorporateGroup, refreshAll],
  )

  const onSubmit = async (data) => {
    try {
      setLoadingUsers(true)
      await service.dponet.ssoCorporateGroupUsers.create({
        ssoCorporateGroupId: ssoCorporateGroup.id,
        userId: data.userId,
      })
      snackbar.open({
        message: 'Usuário vinculado com sucesso!',
        variant: 'success',
      })
      setRefreshAll(!refreshAll)
    } catch (error) {
      return snackbar.open({
        message: helpers.formatters.errorMessage(
          error?.response?.data?.error || 'Erro ao vincular usuário!',
        ),
        variant: 'error',
      })
    } finally {
      setLoadingUsers(false)
    }
  }

  const loadUsers = async () => {
    try {
      setLoadingUsers(true)
      const usersResponse = await service.backoffice.users.get({
        minimal: true,
        perPage: 1000 * 1000,
        name: userInputValue,
        email: ssoCorporateGroup?.domain,
      })
      setUsers(usersResponse?.data?.users)
    } catch (error) {
      console.error(error)
      snackbar.open({
        message: helpers.formatters.errorMessage(
          error?.response?.data?.error || 'Erro ao buscar usuários',
        ),
        variant: 'error',
      })
    } finally {
      setLoadingUsers(false)
    }
  }

  const handleRefresh = () => {
    setRefreshAll(!refreshAll)
    refresh()
  }

  useEffect(() => {
    const getUsers = setTimeout(() => {
      if (userInputValue.length < 3) return

      loadUsers()
    }, 1000)

    return () => clearTimeout(getUsers)
    // eslint-disable-next-line
  }, [userInputValue])

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <LoadingFeedback open={isLoading} />
      <Card title={'Gerenciamento de Usuário'}>
        {!show && (
          <Grid container spacing={2}>
            <Grid item xs={isMobile ? 12 : 10}>
              <Box mt={1}>
                <Controller
                  control={control}
                  name="userId"
                  onChange={([, data]) => data?.id}
                  mode="onChange"
                  as={
                    <Autocomplete
                      options={users || []}
                      noOptionsText={instructionsText(userInputValue)}
                      getOptionLabel={(option) => option.name || ''}
                      getOptionSelected={(option, value) =>
                        option.id === value.id || value.id === 0
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          onChange={handleTypeUsersInput}
                          label="Vincular usuários"
                          variant="outlined"
                          error={!!errors.userId}
                          helperText={errors?.userId?.message}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingUsers && (
                                  <CircularProgress color="primary" size={20} />
                                )}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  }
                />
              </Box>
            </Grid>
            <Grid item xs={isMobile ? 12 : 2}>
              <Box
                height="100%"
                display="flex"
                justifyContent="center"
                alignItems="center"
              >
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  fullWidth
                >
                  Adicionar
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Alert severity="warning">
                <Typography>
                  Ao vincular um usuário a este grupo, ele será automaticamente
                  associado a todas as empresas e organizações pertencentes ao
                  Grupo Empresarial SSO-SAML.
                </Typography>
              </Alert>
              <Box mt={1}>
                <Alert severity="info">
                  <Typography>
                    Caso o usuário não seja encontrado, é provável que ele ainda
                    não exista no sistema. Nesse caso, ele pode ser criado
                    manualmente através do backoffice ou, de forma mais simples,
                    basta orientar o usuário a realizar o login via SSO, o que
                    resultará na criação ou ativação automática do cadastro.
                  </Typography>
                </Alert>
              </Box>
            </Grid>
          </Grid>
        )}
        <ManageUsersTable
          ssoCorporateGroupId={ssoCorporateGroup?.id}
          ssoCorporateGroupUsers={response?.data?.ssoCorporateGroupUsers}
          totalCount={response?.data?.meta?.totalCount}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          page={page}
          perPage={perPage}
          refresh={handleRefresh}
          show={show}
        />
      </Card>
    </form>
  )
}

export default ManageUsersForm
